import { Component, NgModuleRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { HorizonToastService } from '@bayclubs/horizon';
import { TextMaskService } from '@bayclubs/ui-text-mask';
import { FormService, ModalService, TimeService, utils } from '@bayclubs/utils';

import {
    GlobalEventsService,
    GroupClassesSecurityService,
    SlotModel,
    SlotProviderService
} from '../../../infrastructure/infrastructure.module';

import { HttpErrorResponse } from '@angular/common/http';
import { CategoryModel, ClubModel, ContextModel, CreateGroupClassCommand, EditGroupClassCommand, EnumerationModel, GetChangedFieldsQuery, GroupClassChangedFieldsModel, GroupClassDetailsModel, InstructorModel, LocationModel } from '@bayclubs/group-classes-employee-api';
import { TranslateService } from '@ngx-translate/core';
import { ManagementContextResolverService } from '@shared/services/management-context-resolver.service';
import { DataFilteringService, GroupClassesApiService } from '@shared/shared.module';
import { ErrorTranslationService } from '../../../infrastructure/services/error-translation.service';
import { moreThanZeroValidator } from '../../../shared/validators/more-than-zero-validator.directive';
import { RecurringClassActionConfirmationComponent } from '../recurring-class-action-confirmation/recurring-class-action-confirmation.component';

@Component({
    selector: 'app-group-class-save',
    templateUrl: './group-class-save.component.html',
    styleUrls: ['./group-class-save.component.scss'],
    standalone: false
})
export class GroupClassSaveComponent implements OnInit, OnDestroy {
    private static readonly capacityDefaultValue = 20;

    close: Function;

    private nullLocation = { name: 'No location', id: null, type: <unknown>'Regular' } as LocationModel;
    public clubLocations: LocationModel[] = [];
    public locationGroupDisplayNameFunc: (group: string) => string = (group) => group == 'Court' ? 'Courts' : 'Group classes';

    private nullCategory = { name: '', id: null, code: '', classTypeId: '', classTypeName: '' } as CategoryModel;
    public categories: CategoryModel[] = [];

    public isLoaded = false;
    public isGroupClassSaving = false;

    // input parameters
    public id: string;
    public clubId: string;
    public locationId: string;
    public timeFromInMinutes: number;
    public timeToInMinutes: number;
    public date: utils.BcDate;

    public clubs: ClubModel[];
    public revenueDepartments: EnumerationModel[];

    public classForm: UntypedFormGroup;

    public context: ContextModel = {
        clubs: [],
        categories: [],
        statuses: [],
        locations: [],
        bookingWindowUnits: [],
        instructors: [],
        classTypes: [],
        streamingServices: [],
        clubGroupClassCategories: [],
        adpPayPeriods: [],
        revenueDepartments: []
    };

    public existingNames: string[] = [];
    public startSlots: SlotModel[] = [];
    public endSlots: SlotModel[] = [];

    billableTimeInMinutesOptions = [
        { value: 30, label: '30 minutes' },
        { value: 60, label: '60 minutes' },
        { value: 90, label: '90 minutes' }
    ];

    durationTimeInMinutesOptions = [
        { value: 25, label: '25 minutes' },
        { value: 50, label: '50 minutes' },
        { value: 75, label: '75 minutes' }
    ];

    private previousSelectedClubId: string;
    public selectedInstructorModel: InstructorModel;
    private selectedLocationModel: LocationModel | undefined;
    private loadedClassDetails: GroupClassDetailsModel;

    private subscription: Subscription = new Subscription();

    constructor(
        public mask: TextMaskService,
        private modalService: ModalService,
        private formBuilder: UntypedFormBuilder,
        private slotProvider: SlotProviderService,
        private groupClassesApi: GroupClassesApiService,
        private toast: HorizonToastService,
        private globalEvents: GlobalEventsService,
        private contextResolverService: ManagementContextResolverService,
        private security: GroupClassesSecurityService,
        private filteringService: DataFilteringService,
        private timeService: TimeService,
        private moduleRef: NgModuleRef<any>,
        private translateService: TranslateService,
        private errorTranslation: ErrorTranslationService
    ) {}

    ngOnInit() {
        this.subscription.add(
            this.contextResolverService.refreshContext().subscribe(context => {
                this.context = context;
                this.revenueDepartments = context?.revenueDepartments?.slice().sort((a,b) => a.code.localeCompare(b.code))
                    .map<EnumerationModel>(x => ({
                        code: x.code,
                        name: `${x.code} ${x.name}`
                    }));
                this.init();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private init() {

        this.initForm();
        this.initSlots();
        this.initClubs();

        this.groupClassesApi.getNames().subscribe(names => {
            this.existingNames = names;

            if (this.isEdit) {
                this.groupClassesApi.getDetails(this.id, false).subscribe(classDetails => {
                    this.isLoaded = true;
                    this.initFormValues(classDetails);
                    this.setInitialValidation();
                    this.selectedInstructorModel = classDetails.instructor;
                });
                this.classForm.controls['recurring'].disable();
            } else {
                this.isLoaded = true;
            }
        });
    }

    private initClubs() {
        this.security.filterClubsForManageClasses(this.context.clubs).subscribe(filtered => {
            this.clubs = filtered;
        });
    }

    private getClubLocations(clubId: string): LocationModel[] {
        const locations = this.context.locations.filter(x => x.clubId === clubId);
        locations.splice(0, 0, this.nullLocation);
        locations.sort((a, b) => a.order - b.order);
        return locations;
    }

    private getClubCategories(clubId: string): CategoryModel[] {
        const categories = this.filteringService.getEnabledAvailableCategoriesByClubs([clubId], this.context);
        return [...categories];
    }

    public get isEdit() {
        return !!this.id;
    }

    public get isClubDisabled() {
        return this.isEdit;
    }

    public get startDateSelected() {
        return this.classForm.controls['classDate'].value !== null;
    }

    public onSave() {
        this.isGroupClassSaving = true;
        FormService.markFormAsTouched(this.classForm);        
        if (!this.classForm.valid) {
            this.isGroupClassSaving = false;
            return;
        }
        if (this.isEdit) {
            this.editGroupClass();
        } else {
            this.createGroupClass();
        }  
    }

    private createGroupClass() {
        const request = this.getCreateRequest();
        this.groupClassesApi.save(request).subscribe(x => {
            this.isGroupClassSaving = false;
            this.globalEvents.onGroupClassAdded$.emit({
                ids: x.items.map(i => i.id)
            });
            this.showOperationSuccess();
            this.close();
        }, error => {
            this.isGroupClassSaving = false;
            this.showError(error);
        });
    }

    private editNormally(): boolean {
        if (this.loadedClassDetails?.isRecurring) {
            this.groupClassesApi.detectFutureOccurrences(this.id).subscribe(futureOccurrencesExists => {
                if (futureOccurrencesExists) {
                    this.groupClassesApi
                        .getChangedFields(this.getChangedFieldsRequest())
                        .subscribe((changedFields: GroupClassChangedFieldsModel) => {
                            if (changedFields.changedFields.length > 0 && !changedFields.dateChanged) {
                                this.editRecurringClass(changedFields.changedFields);
                            } else {
                                this.saveEditedGroupClass(false);
                            }
                        });
                } else {
                    this.saveEditedGroupClass(false);
                }
            });
        } else {
            return true;
        }
    }

    private getChangedFieldsRequest(): GetChangedFieldsQuery {
        const request = <GetChangedFieldsQuery>{
            date: this.classForm.get('classDate').value,
            id: this.id
        };

        this.fillBaseRequest(request);
        return request;
    }
    private editRecurringClass(changedFields: string[]) {
        const event = { isRecurring: this.loadedClassDetails.isRecurring, changedFields: changedFields };
        const bodyText: string = this.translateService.instant('GroupClassSave.EditRecurringConfirmationText');

        const headerText: string = this.translateService.instant('GroupClassSave.EditHeader');

        const params = {
            event,
            warningBar: false,
            bodyText: bodyText,
            headerText: headerText,
            confirmed: includeFollowing => {
                this.saveEditedGroupClass(includeFollowing);
            }
        };

        this.modalService
            .create<RecurringClassActionConfirmationComponent>(
                RecurringClassActionConfirmationComponent,
                params,
                this.moduleRef,
                '#confirmationModal'
            )
            .subscribe(() => {});
    }

    private editGroupClass() {
        if (this.editNormally()) {
            this.saveEditedGroupClass(false);
        }
    }

    private saveEditedGroupClass(includeFollowing: boolean) {
        const request = this.getEditRequest(includeFollowing);
        this.groupClassesApi.edit(request).subscribe(x => {
            this.isGroupClassSaving = false;
            this.globalEvents.onGroupClassUpdated$.emit({ id: request.id });
            this.showOperationSuccess();
            this.close();
        }, error => {
            this.isGroupClassSaving = false;
            this.showError(error);
        });
    }

    public onClose() {
        this.close();
    }

    public onVisibleToConnectChanged(isVisibleToConnect: boolean) {
        this.classForm.get('isVisibleToConnect').setValue(isVisibleToConnect);
    }

    public onBookingWindowApplicableChanged(isBookingWindowApplicable: boolean) {
        this.classForm.get('isBookingWindowApplicable').setValue(isBookingWindowApplicable);
    }

    public onBookingRequiredChanged($event: boolean) {
        this.classForm.get('isBookingRequired').setValue($event);

        if (this.isBookingRequired) {
            this.classForm.get('capacity').setValue(GroupClassSaveComponent.capacityDefaultValue);
            this.classForm.get('capacity').setValidators(Validators.required);
            this.classForm.get('capacity').updateValueAndValidity();
        } else {
            this.classForm.get('capacity').setValue(null);
            this.classForm.get('capacity').setValidators(null);
            this.classForm.get('capacity').updateValueAndValidity();
            this.classForm.get('isBookingWindowApplicable').setValue(false);
            this.classForm.get('bookingWindowValue').setValue(null);
            this.classForm.get('bookingWindowValue').setValidators(null);
            this.classForm.get('bookingWindowValue').updateValueAndValidity();
            this.classForm.get('bookingWindowUnitCode').setValue(null);
            this.classForm.get('bookingWindowUnitCode').setValidators(null);
            this.classForm.get('bookingWindowUnitCode').updateValueAndValidity();
            this.classForm.get('isWaitListAllowed').setValue(false);
            this.classForm.get('isWaitListAllowed').setValidators(null);
            this.classForm.get('isWaitListAllowed').updateValueAndValidity();

            this.resetIsBookingRequiresSpecificResourceValue();
        }
    }

    public onFeeBasedChanged($event: boolean) {
        this.classForm.get('isFeeBased').setValue($event);
        if ($event) {
            this.classForm.get('fee').setValidators([Validators.required, moreThanZeroValidator]);
            this.classForm.get('fee').updateValueAndValidity();
            this.classForm.get('revenueDepartmentCode').setValidators(Validators.required);
            this.classForm.get('revenueDepartmentCode').updateValueAndValidity();
        }
        else {
            this.classForm.get('fee').setValue(null);
            this.classForm.get('fee').setValidators(null);
            this.classForm.get('fee').updateValueAndValidity();
            this.classForm.get('fee').markAsUntouched();

            this.classForm.get('revenueDepartmentCode').setValue(null);
            this.classForm.get('revenueDepartmentCode').setValidators(null);
            this.classForm.get('revenueDepartmentCode').updateValueAndValidity();
        }
        
    }

    public onOnlineChanged($event: boolean) {
        this.classForm.get('isOnline').setValue($event);

        if ($event) {
            this.onBookingRequiredChanged(false);
        }

        this.setOnlineValidation();
    }

    onFeeChanged($event: any) {
        const value = $event?.target?.value
        if (!!value && Number.parseFloat(value) > 0) {
            this.classForm.get('isBookingRequired').setValue(true);
            this.classForm.get('isBookingRequired').updateValueAndValidity();
        }
    }

    private setOnlineValidation() {
        if (this.isOnline) {
            this.classForm.get('streamUrl').setValidators(Validators.required);
        } else {
            this.classForm.get('streamUrl').setValidators(null);
        }
        this.classForm.get('streamUrl').updateValueAndValidity();
    }

    public get selectedDateIsFromThePast(): boolean {
        const club = this.context.clubs.find(x => x.id === this.clubId);
        if (!club) {
            return false;
        }

        const nowInTimezone = this.timeService.todayInTimeZone(club.timezone);

        const date = this.classForm.get('classDate').value;
        if (date) {
            const m = this.timeService.toMoment(date);
            const isFromPast = m.isBefore(nowInTimezone, 'day');
            return isFromPast;
        } else {
            return false;
        }
    }

    public get isBookingWindowApplicable(): boolean {
        return this.classForm.get('isBookingWindowApplicable').value;
    }
    public get isBookingRequired(): boolean {
        return this.classForm.get('isBookingRequired').value;
    }

    public get isBookingRequiredDisabled(): boolean {
        return this.isOnline;
    }

    public get isOnline(): boolean {
        return this.classForm.get('isOnline').value;
    }

    public get isBookingRequiresSpecificResourceApplicable(): boolean {
        if (!this.selectedLocationModel) {
            return false;
        }

        return (
            this.isBookingRequired &&
            this.selectedLocationModel.hasLayoutDefined &&
            this.selectedLocationModel.numberOfResources > 0
        );
    }

    public get isBookingRequiresSpecificResourceDisabled(): boolean {
        return this.classForm.get('isAtLeastOneActiveBooking').value;
    }

    public get isBookingRequiresSpecificResource(): boolean {
        return this.classForm.get('isBookingRequiresSpecificResource').value;
    }

    private resetIsBookingRequiresSpecificResourceValue() {
        this.classForm.get('isBookingRequiresSpecificResource').setValue(null);
        this.classForm.get('isBookingRequiresSpecificResource').setValidators(null);
        this.classForm.get('isBookingRequiresSpecificResource').updateValueAndValidity();
    }

    public get isFeeBased(): boolean {
        return this.classForm.get('isFeeBased').value;
    }

    get isCategoriesDisabled(): boolean {
        return !this.classForm.controls['clubId'].valid;
    }

    get isCategorySelected(): boolean {
        return this.classForm.controls['category'].valid;
    }

    get showTimePicker(): boolean {
        if(!this.isCategorySelected) {
            return false;
        }

        const clubId = this.classForm.controls['clubId'].value;
        const category = this.classForm.controls['category'].value;

        const selectedCategory = this.context.clubGroupClassCategories.find(x => x.clubId == clubId
            && x.categoryCode == category.code);

        return selectedCategory == null || selectedCategory.jobCostCode == null;
    }

    public get isOnlineDisabled(): boolean {
        return this.isEdit;
    }

    public get revenueDepartmentCode(): string {
        return this.classForm.get('revenueDepartmentCode').value;
    }

    public get showRevenueDepartment(): boolean {
        const fee = this.classForm.get('fee').value;
        return !!fee && fee > 0;
    }

    private initForm() {
        const defaultTimeFromInMinutes = 660;
        const defaultTimeToInMinutes = 690;
        const defaultDurationTimeInMinutes = 25;
        const defaultBillableTimeInMinutes = 30;

        this.classForm = this.formBuilder.group({
            name: [null, [Validators.required, Validators.maxLength(200)]],
            capacity: [GroupClassSaveComponent.capacityDefaultValue, [Validators.required]],
            clubId: [null, [Validators.required]],
            category: [null, [Validators.required]],
            description: [null, [Validators.maxLength(2000)]],
            locationId: [null],
            fee: [null],
            isVisibleToConnect: [false, [Validators.required]],
            timeFromInMinutes: [defaultTimeFromInMinutes, [Validators.required]],
            timeToInMinutes: [defaultTimeToInMinutes, []],
            durationTimeInMinutes: [defaultDurationTimeInMinutes, [Validators.required]],
            billableTimeInMinutes: [defaultBillableTimeInMinutes, [Validators.required]],
            isBookingWindowApplicable: [false, [Validators.required]],
            isBookingRequired: [false, [Validators.required]],
            bookingWindowUnitCode: [null],
            bookingWindowValue: [null],
            recurring: [null],
            isWaitListAllowed: [false],
            isBookingRequiresSpecificResource: [false],
            isAtLeastOneActiveBooking: [false],
            isOnline: [false],
            streamingService: [null],
            streamUrl: [null, [Validators.required]],
            revenueDepartmentCode: [null],
            isFeeBased: [false]
        });

        this.classForm.addControl('classDate', new UntypedFormControl(null, [Validators.required]));

        this.classForm.controls['clubId'].valueChanges.subscribe((selectedValue: string) => {
            if (this.previousSelectedClubId !== selectedValue) {
                this.resetClubRelatedValues();
            }

            this.clubLocations = this.getClubLocations(selectedValue);
            this.categories = this.getClubCategories(selectedValue);

            this.previousSelectedClubId = selectedValue;
            this.clubId = selectedValue;
        });

        this.classForm.controls['locationId'].valueChanges.subscribe((selectedValue: LocationModel | undefined) => {
            this.selectedLocationModel = this.clubLocations.find(c => c.id === selectedValue?.id) ?? undefined;
            this.resetIsBookingRequiresSpecificResourceValue();
        });


        this.classForm.controls['category'].valueChanges.subscribe((value) => {
            console.log('category = ', this.showTimePicker)
            if(this.showTimePicker) {
                this.classForm.get('timeToInMinutes').setValidators(Validators.required);
                this.classForm.get('timeToInMinutes').updateValueAndValidity();
                this.classForm.get('durationTimeInMinutes').setValidators(null);
                this.classForm.get('durationTimeInMinutes').updateValueAndValidity();
            } else {
                this.classForm.get('timeToInMinutes').setValidators(null);
                this.classForm.get('timeToInMinutes').updateValueAndValidity();
                this.classForm.get('durationTimeInMinutes').setValidators(Validators.required);
                this.classForm.get('durationTimeInMinutes').updateValueAndValidity();
            }
        });

        //

        this.classForm.controls['isBookingRequiresSpecificResource'].valueChanges.subscribe((selectedValue: boolean) => {
            if (selectedValue == null) {
                return;
            }

            const capacityValue = selectedValue
                ? this.selectedLocationModel.numberOfResources
                : this.classForm.get('capacity').value;

            this.classForm.get('capacity').setValue(capacityValue);
            this.classForm.get('capacity').setValidators(Validators.required);
            this.classForm.get('capacity').updateValueAndValidity();
        });

        this.fillFormFromInputParameters();
        this.setOnlineValidation();
    }

    private resetClubRelatedValues() {
        this.classForm.controls['locationId'].reset();
        this.classForm.controls['category'].reset();
        this.selectedInstructorModel = null;
        this.selectedLocationModel = null;
    }

    public onInstructorSelectedSelected(item: InstructorModel): void {
        this.selectedInstructorModel = item;
    }

    private fillFormFromInputParameters(): void {
        if (this.clubId) {
            this.classForm.controls['clubId'].setValue(this.clubId);
        }

        if (this.locationId) {
            const location = this.clubLocations.find(x => x.id == this.locationId);
            this.classForm.controls['locationId'].setValue(location);
        }

        if (this.date) {
            this.classForm.controls['classDate'].setValue(this.date);
        }

        if (this.timeFromInMinutes) {
            this.classForm.controls['timeFromInMinutes'].setValue(this.timeFromInMinutes);
        }

        if (this.timeToInMinutes) {
            this.classForm.controls["timeToInMinutes"].setValue(this.timeToInMinutes);

            const defaultDurationTimeInMinutes = this.timeToInMinutes - this.timeFromInMinutes;

            this.classForm.controls['durationTimeInMinutes'].setValue(defaultDurationTimeInMinutes);
        }

        if (this.timeFromInMinutes && this.timeToInMinutes) {
            const defaultBillableTimeInMinutes = this.timeToInMinutes - this.timeFromInMinutes;
            this.classForm.controls['billableTimeInMinutes'].setValue(defaultBillableTimeInMinutes);
        }
    }

    private getCreateRequest(): CreateGroupClassCommand {
        const request = <CreateGroupClassCommand>{
            date: this.classForm.get('classDate').value,
            ends: this.classForm.get('recurring').value
        };

        this.fillBaseRequest(request);
        return request;
    }

    private getEditRequest(includeFollowing: boolean): EditGroupClassCommand {
        const request = <EditGroupClassCommand>{
            date: this.classForm.get('classDate').value,
            id: this.id,
            updateFollowingRecurringClasses: includeFollowing
        };

        this.fillBaseRequest(request);
        return request;
    }

    private fillBaseRequest(request: CreateGroupClassCommand | EditGroupClassCommand) {
        request.name = this.classForm.get('name').value;

        request.clubId = this.classForm.get('clubId').value;
        request.categoryCode = this.classForm.get('category').value.code;
        request.description = this.classForm.get('description').value;
        request.locationId = this.classForm.get('locationId').value?.id || this.nullLocation.id;
        request.fee = +this.classForm.get('fee').value;
        request.instructorId = this.selectedInstructorModel ? this.selectedInstructorModel.id : null;
        request.isVisibleToConnect = this.classForm.get('isVisibleToConnect').value;
        request.timeFromInMinutes = this.classForm.get('timeFromInMinutes').value;
        request.timeToInMinutes = this.showTimePicker == false
          ? (this.classForm.get('timeFromInMinutes').value + this.classForm.get('durationTimeInMinutes').value)
          : this.classForm.get('timeToInMinutes').value;
        request.billableTimeInMinutes = this.classForm.get('billableTimeInMinutes').value;
        request.isBookingWindowApplicable = this.classForm.get('isBookingWindowApplicable').value;
        request.isBookingRequired = this.classForm.get('isBookingRequired').value;
        request.capacity =
            this.isBookingRequiresSpecificResource || !this.isBookingRequired ? null : this.classForm.get('capacity').value;
        if (request.isBookingWindowApplicable) {
            request.bookingWindowUnitCode = this.classForm.get('bookingWindowUnitCode').value;
            request.bookingWindowValue = this.classForm.get('bookingWindowValue').value;
        }
        request.isWaitListAllowed =
            this.classForm.get('isWaitListAllowed').value !== null ? this.classForm.get('isWaitListAllowed').value : false;
        request.isBookingRequiresSpecificResource =
            this.classForm.get('isBookingRequiresSpecificResource').value !== null
                ? this.classForm.get('isBookingRequiresSpecificResource').value
                : false;
        request.classTypeId = this.getClassTypeIdFromCategoryCode(request.categoryCode);

        request.isOnline = this.classForm.get('isOnline').value;
        if (this.isOnline) {
            request.streamingServiceCode = this.classForm.get('streamingService').value;
            request.streamUrl = this.classForm.get('streamUrl').value;
        }

        request.revenueDepartmentCode = this.classForm.get('revenueDepartmentCode').value;
    }

    private getClassTypeIdFromCategoryCode(categoryCode: string): string {
        const category = this.categories.find(c => c.code === categoryCode);
        return category.classTypeId;
    }

    private initFormValues(classDetails: GroupClassDetailsModel) {
        this.date = classDetails.date;
        this.setFromValue('name', classDetails.name);
        this.setFromValue('capacity', classDetails.capacity);
        this.setFromValue('clubId', classDetails.club.id);
        this.setFromValue('category', classDetails.category);
        this.setFromValue('description', classDetails.description);
        this.setFromValue('locationId', classDetails.location || this.nullLocation);
        this.setFromValue('fee', classDetails.fee);
        this.setFromValue('capacity', classDetails.capacity);
        this.setFromValue('timeFromInMinutes', classDetails.timeFromInMinutes);
        this.setFromValue('timeToInMinutes', classDetails.timeToInMinutes);
        this.setFromValue('isFeeBased', !!classDetails.fee && classDetails.fee > 0);

        const durationInMinutes = classDetails.timeToInMinutes - classDetails.timeFromInMinutes;
        const existingDurationInMinutesOption = this.durationTimeInMinutesOptions.find(x => x.value == durationInMinutes);
        if(!existingDurationInMinutesOption) {
            this.durationTimeInMinutesOptions.push({
                value: durationInMinutes,
                label: `${durationInMinutes} minutes`
            });

            this.durationTimeInMinutesOptions = this.durationTimeInMinutesOptions.sort((a,b)=> (a.value < b.value ? -1 : 1));
        }

        this.setFromValue('durationTimeInMinutes', durationInMinutes);

        if(classDetails.billableTimeInMinutes) {
            const existingBillableTimeInMinutesOption = this.billableTimeInMinutesOptions.find(x => x.value == classDetails.billableTimeInMinutes);
            if(!existingBillableTimeInMinutesOption) {
                this.billableTimeInMinutesOptions.push({
                    value: classDetails.billableTimeInMinutes,
                    label: `${classDetails.billableTimeInMinutes} minutes`
                });

                this.billableTimeInMinutesOptions = this.billableTimeInMinutesOptions.sort((a,b)=> (a.value < b.value ? -1 : 1));
            }

            this.setFromValue('billableTimeInMinutes', classDetails.billableTimeInMinutes);
        }


        this.setFromValue('classDate', classDetails.date);
        this.setFromValue('isVisibleToConnect', classDetails.isVisibleToConnect);

        this.setFromValue('isBookingWindowApplicable', classDetails.isBookingWindowApplicable);
        this.setFromValue('isBookingRequired', classDetails.isBookingRequired);
        this.setFromValue(
            'bookingWindowUnitCode',
            classDetails.bookingWindowUnit != null ? classDetails.bookingWindowUnit.code : null
        );
        this.setFromValue('bookingWindowValue', classDetails.bookingWindowValue);
        this.setFromValue('recurring', classDetails.recurringDefinition);
        this.setFromValue(
            'isWaitListAllowed',
            classDetails.isWaitListAllowed !== null ? classDetails.isWaitListAllowed : false
        );
        this.setFromValue(
            'isBookingRequiresSpecificResource',
            classDetails.isBookingRequiresSpecificResource !== null ? classDetails.isBookingRequiresSpecificResource : false
        );
        this.setFromValue('isAtLeastOneActiveBooking', classDetails.isAtLeastOneActiveBooking);

        const isOnline = classDetails.classOnline !== null ? classDetails.classOnline.isOnline : false;
        this.setFromValue('isOnline', isOnline);
        if (isOnline === true) {
            if (classDetails.classOnline !== null && classDetails.classOnline.streamingService !== null) {
                this.setFromValue('streamingService', classDetails.classOnline.streamingService.code);
            }
            this.setFromValue('streamUrl', classDetails.classOnline.streamUrl);
        }

        this.setFromValue('revenueDepartmentCode', classDetails?.revenueDepartment?.code);

        this.loadedClassDetails = classDetails;
    }

    private setInitialValidation() {
        if (this.isBookingRequired) {
            this.classForm.get('capacity').setValidators(Validators.required);
            this.classForm.get('capacity').updateValueAndValidity();
        } else {
            this.classForm.get('capacity').setValidators(null);
            this.classForm.get('capacity').updateValueAndValidity();
            this.classForm.get('bookingWindowValue').setValidators(null);
            this.classForm.get('bookingWindowValue').updateValueAndValidity();
            this.classForm.get('bookingWindowUnitCode').setValidators(null);
            this.classForm.get('bookingWindowUnitCode').updateValueAndValidity();
        }
        this.setOnlineValidation();
    }

    private initSlots() {
        this.startSlots = this.slotProvider.getDailySlots();

        this.classForm.controls['timeFromInMinutes'].valueChanges.subscribe(value => {
            this.endSlots = this.slotProvider.getDailySlotsFrom(+value);
        });
    }

    private setFromValue(fromControlName: string, value: any) {
        this.classForm.get(fromControlName).setValue(value);
    }

    private showError(error: HttpErrorResponse) {
        this.toast.error(this.errorTranslation.getTranslatedMessage(error.error))
    }

    private showOperationSuccess() {
        this.toast.success(this.translateService.instant('Utils.OperationOk'));
    }
}
